import { Button, Checkbox, NumberInput, TextInput } from "@mantine/core";
import {
  IconCheck,
  IconFileText,
  IconLink,
  IconSearch,
  IconShoppingCart,
  IconX,
} from "@tabler/icons-react";
import {
  type RfqLineInput,
  type RfqProductOption,
  loadPurchaseOrderLines,
  loadPurchaseOrderSources,
} from "@/lib/rfq/api";
import { useEffect, useMemo, useState } from "react";

import { Modal } from "antd";
import { useRfqLabels } from "@/lib/rfq/labels";

interface SourceOrder {
  id: string;
  name: string;
  partnerId: string;
  purchaseDate: string;
  status: string;
}

interface SourceLine {
  id: string;
  productId: string;
  amount: number;
}

interface PickedLine {
  orderId: string;
  orderName: string;
  productId: string;
  productName: string;
  spec: string;
  unit: string;
  sourceQty: number;
  qty: number;
  dueDate: string;
}

function todayInput(): string {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  const day = `${now.getDate()}`.padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

function RfqImportModal({
  open,
  onClose,
  products,
  onImport,
}: {
  open: boolean;
  onClose: () => void;
  products: RfqProductOption[];
  onImport: (
    inputs: Omit<RfqLineInput, "seq">[],
    sourceOrderIds: string[],
  ) => Promise<void> | void;
}) {
  const { t } = useRfqLabels();
  const [orders, setOrders] = useState<SourceOrder[]>([]);
  const [activeOrder, setActiveOrder] = useState<SourceOrder | null>(null);
  const [orderLines, setOrderLines] = useState<SourceLine[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingLines, setLoadingLines] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [picked, setPicked] = useState<Record<string, PickedLine>>({});
  const [saving, setSaving] = useState(false);

  const productMap = useMemo(() => {
    const map = new Map<string, RfqProductOption>();
    for (const product of products) map.set(product.id, product);
    return map;
  }, [products]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingOrders(true);
    loadPurchaseOrderSources()
      .then((rows) => {
        if (cancelled) return;
        setOrders(rows);
        setActiveOrder(rows[0] ?? null);
      })
      .finally(() => {
        if (!cancelled) setLoadingOrders(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!activeOrder) {
      setOrderLines([]);
      return;
    }
    let cancelled = false;
    setLoadingLines(true);
    loadPurchaseOrderLines(activeOrder.id)
      .then((rows) => {
        if (!cancelled) setOrderLines(rows);
      })
      .finally(() => {
        if (!cancelled) setLoadingLines(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeOrder]);

  const filteredOrders = useMemo(() => {
    const value = keyword.trim().toLowerCase();
    if (!value) return orders;
    return orders.filter(
      (order) => order.name.toLowerCase().indexOf(value) >= 0,
    );
  }, [orders, keyword]);

  const touchedOrders = useMemo(() => {
    const set = new Set<string>();
    Object.values(picked).forEach((item) => set.add(item.orderId));
    return set;
  }, [picked]);

  const pickKey = (orderId: string, lineId: string) => `${orderId}:${lineId}`;

  const togglePick = (line: SourceLine) => {
    if (!activeOrder) return;
    const key = pickKey(activeOrder.id, line.id);
    const product = productMap.get(line.productId);
    setPicked((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else
        next[key] = {
          orderId: activeOrder.id,
          orderName: activeOrder.name,
          productId: line.productId,
          productName: product?.name ?? line.productId,
          spec: [product?.spec, product?.material].filter(Boolean).join(" ・ "),
          unit: product?.unit ?? "",
          sourceQty: line.amount,
          qty: line.amount,
          dueDate: todayInput(),
        };
      return next;
    });
  };

  const patchPick = (key: string, value: Partial<PickedLine>) =>
    setPicked((prev) =>
      prev[key] ? { ...prev, [key]: { ...prev[key], ...value } } : prev,
    );

  const allChecked =
    !!activeOrder &&
    orderLines.length > 0 &&
    orderLines.every((line) => picked[pickKey(activeOrder.id, line.id)]);

  const toggleAll = () => {
    if (!activeOrder) return;
    if (allChecked) {
      setPicked((prev) => {
        const next = { ...prev };
        orderLines.forEach((line) => {
          delete next[pickKey(activeOrder.id, line.id)];
        });
        return next;
      });
      return;
    }
    orderLines.forEach((line) => {
      if (!picked[pickKey(activeOrder.id, line.id)]) togglePick(line);
    });
  };

  const basket = Object.entries(picked);

  const basketByOrder = useMemo(() => {
    const map = new Map<string, [string, PickedLine][]>();
    basket.forEach(([key, item]) => {
      if (!map.has(item.orderName)) map.set(item.orderName, []);
      (map.get(item.orderName) as [string, PickedLine][]).push([key, item]);
    });
    return Array.from(map.entries());
  }, [basket]);

  const doImport = async () => {
    setSaving(true);
    try {
      await onImport(
        basket.map(([, item]) => {
          const product = productMap.get(item.productId);
          return {
            productId: item.productId,
            quantity: item.qty,
            priceMode:
              product?.category === "material"
                ? ("weight" as const)
                : ("unit" as const),
            requiredDeliveryDate: item.dueDate,
            materialCert: false,
            remark: "",
            targetUnitPrice: null,
          };
        }),
        Array.from(new Set(basket.map(([, item]) => item.orderId))),
      );
      setPicked({});
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={1100}
      footer={null}
      destroyOnClose
      styles={{ body: { padding: 0 } }}
      title={
        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-2 text-base font-medium text-gray-700">
            <IconLink size={18} className="text-blue-600" />
            {t("rfq.importModal.title") || "從採購單帶入明細"}
          </span>
          <span className="text-xs font-normal text-gray-500">
            {t("rfq.importModal.description") ||
              "可跨多張採購單挑選項目，帶入後規格唯讀、數量可調整。"}
          </span>
        </div>
      }
    >
      <div className="grid grid-cols-1 border-t border-gray-200 md:grid-cols-[240px_1fr_300px]">
        <div className="flex max-h-[60vh] min-h-0 flex-col border-b border-gray-200 md:border-b-0 md:border-r">
          <div className="border-b border-gray-200 p-3">
            <TextInput
              value={keyword}
              onChange={(event) => setKeyword(event.currentTarget.value)}
              placeholder={t("rfq.importModal.search") || "搜尋單號 / 專案"}
              leftSection={<IconSearch size={15} />}
              aria-label={t("rfq.importModal.search") || "搜尋單號 / 專案"}
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            {loadingOrders ? (
              <p className="mt-8 text-center text-sm text-gray-500">
                {t("rfq.importModal.loading") || "載入中…"}
              </p>
            ) : filteredOrders.length === 0 ? (
              <p className="mt-8 text-center text-sm text-gray-500">
                {t("rfq.importModal.noOrder") || "查無採購單。"}
              </p>
            ) : (
              filteredOrders.map((order) => {
                const active = order.id === activeOrder?.id;
                return (
                  <button
                    key={order.id}
                    type="button"
                    onClick={() => setActiveOrder(order)}
                    className={`mb-1 flex w-full flex-col gap-0.5 rounded-lg border px-3 py-2 text-left transition-colors ${
                      active
                        ? "border-blue-200 bg-blue-50"
                        : "border-transparent hover:bg-gray-50"
                    }`}
                  >
                    <span className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                      <IconFileText size={14} className="text-gray-400" />
                      {order.name}
                      {touchedOrders.has(order.id) ? (
                        <IconCheck size={14} className="text-emerald-500" />
                      ) : null}
                    </span>
                    <span className="text-[11px] text-gray-400">
                      {order.purchaseDate || "—"}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="flex max-h-[60vh] min-h-0 flex-col border-b border-gray-200 md:border-b-0 md:border-r">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2.5">
            <span className="text-sm font-medium text-gray-700">
              {t("rfq.importModal.linesOf", {
                defaultValue: "{{po}} 明細",
                po: activeOrder?.name ?? "—",
              })}
            </span>
            <Button
              variant="subtle"
              color="gray"
              size="xs"
              disabled={orderLines.length === 0}
              onClick={toggleAll}
            >
              {allChecked
                ? t("rfq.importModal.deselectAll") || "反選"
                : t("rfq.importModal.selectAll") || "全選"}
            </Button>
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
            {loadingLines ? (
              <p className="mt-8 text-center text-sm text-gray-500">
                {t("rfq.importModal.loading") || "載入中…"}
              </p>
            ) : orderLines.length === 0 ? (
              <p className="mt-8 text-center text-sm text-gray-500">
                {t("rfq.importModal.noLine") || "此採購單沒有商品明細。"}
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 text-left text-xs text-gray-500">
                  <tr>
                    <th className="w-9 px-3 py-2"></th>
                    <th className="px-2 py-2 font-medium">
                      {t("rfq.importModal.colNameSpec") || "品名 / 規格"}
                    </th>
                    <th className="px-2 py-2 font-medium">
                      {t("rfq.importModal.colMaterial") || "材質"}
                    </th>
                    <th className="px-2 py-2 font-medium">
                      {t("rfq.importModal.colPoQty") || "採購量"}
                    </th>
                    <th className="px-2 py-2 font-medium">
                      {t("rfq.importModal.colRfqQty") || "詢價量"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {orderLines.map((line) => {
                    const key = pickKey(activeOrder?.id ?? "", line.id);
                    const item = picked[key];
                    const product = productMap.get(line.productId);
                    return (
                      <tr
                        key={line.id}
                        className={`border-b border-gray-100 ${item ? "bg-blue-50/40" : ""}`}
                      >
                        <td className="px-3 py-2">
                          <Checkbox
                            size="xs"
                            checked={!!item}
                            onChange={() => togglePick(line)}
                            aria-label={`${activeOrder?.name ?? ""} ${product?.name ?? line.productId}`}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <div className="font-medium text-gray-700">
                            {product?.name ?? line.productId}
                          </div>
                          <div className="text-xs text-gray-500">
                            {product?.spec || "—"}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-xs text-gray-600">
                          {product?.material || "—"}
                        </td>
                        <td className="px-2 py-2 text-gray-500">
                          {line.amount} {product?.unit ?? ""}
                        </td>
                        <td className="px-2 py-2">
                          {item ? (
                            <NumberInput
                              size="xs"
                              className="w-24"
                              min={0}
                              hideControls
                              value={item.qty}
                              onChange={(value) =>
                                patchPick(key, { qty: Number(value) || 0 })
                              }
                              aria-label={`${product?.name ?? line.productId} ${t("rfq.importModal.colRfqQty") || "詢價量"}`}
                            />
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="flex max-h-[60vh] min-h-0 flex-col bg-gray-50">
          <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-2.5">
            <IconShoppingCart size={16} className="text-blue-600" />
            <span className="text-sm font-medium text-gray-700">
              {t("rfq.importModal.basket") || "已選籃"}
            </span>
            <span className="ml-auto text-xs text-gray-500">
              {t("rfq.importModal.basketCount", {
                defaultValue: "{{orders}} 張單 ・ {{lines}} 筆",
                orders: basketByOrder.length,
                lines: basket.length,
              })}
            </span>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-3">
            {basket.length === 0 ? (
              <p className="mt-8 text-center text-sm text-gray-500">
                {t("rfq.importModal.basketEmpty") ||
                  "勾選中間欄的項目後，會累計顯示在這裡。"}
              </p>
            ) : (
              basketByOrder.map(([orderName, items]) => (
                <div key={orderName} className="mb-3">
                  <div className="mb-1 text-xs font-medium text-gray-500">
                    {orderName}
                  </div>
                  {items.map(([key, item]) => (
                    <div
                      key={key}
                      className="mb-1 flex flex-col gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5"
                    >
                      <div className="flex items-center gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm text-gray-700">
                            {item.productName}
                          </div>
                          <div className="truncate text-[11px] text-gray-500">
                            {item.spec || "—"} ・ {item.qty} {item.unit}
                          </div>
                        </div>
                        <button
                          type="button"
                          aria-label={`${t("rfq.importModal.removePick") || "移除"} ${item.productName}`}
                          className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-red-500"
                          onClick={() =>
                            setPicked((prev) => {
                              const next = { ...prev };
                              delete next[key];
                              return next;
                            })
                          }
                        >
                          <IconX size={14} />
                        </button>
                      </div>
                      <TextInput
                        size="xs"
                        type="date"
                        value={item.dueDate}
                        onChange={(event) =>
                          patchPick(key, { dueDate: event.currentTarget.value })
                        }
                        aria-label={`${item.productName} ${t("rfq.detail.colDueDate") || "需求交期"}`}
                      />
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 border-t border-gray-200 px-4 py-3">
        <Button variant="default" radius="md" onClick={onClose}>
          {t("rfq.importModal.cancel") || "取消"}
        </Button>
        <Button
          radius="md"
          color="blue"
          loading={saving}
          disabled={basket.length === 0}
          onClick={doImport}
        >
          {t("rfq.importModal.importCount", {
            defaultValue: "帶入 {{n}} 筆",
            n: basket.length,
          })}
        </Button>
      </div>
    </Modal>
  );
}

export default RfqImportModal;
